import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { SA_TOKEN_KEY, restoreSuperadmin, TOKEN_KEY, DEVICE_KEY, getDeviceCookie, setDeviceCookie, REMEMBER_DAYS, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [error, setError] = useState("");

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Se stiamo tornando dal callback OAuth, non eseguire /me: AuthCallback gestirà la sessione.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    loadMe();
  }, [loadMe]);

  const login = async (email, password, force = false) => {
    setError("");
    try {
      const device_id = getDeviceCookie() || localStorage.getItem(DEVICE_KEY) || undefined;
      const { data } = await api.post("/auth/login", { email, password, device_id, force });
      if (data.session_conflict) {
        return { ok: false, sessionConflict: true };
      }
      if (data.otp_required) {
        return { ok: false, otpRequired: true, challengeId: data.challenge_id, email: data.email };
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const requestOtp = async (email) => {
    setError("");
    try {
      const device_id = getDeviceCookie() || localStorage.getItem(DEVICE_KEY) || undefined;
      const { data } = await api.post("/auth/otp/request", { email, device_id });
      return { ok: true, challengeId: data.challenge_id, email: data.email };
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      return { ok: false, error: msg };
    }
  };

  const verifyOtp = async (challengeId, code, remember, force = false) => {
    setError("");
    try {
      const device_id = getDeviceCookie() || localStorage.getItem(DEVICE_KEY) || undefined;
      const { data } = await api.post("/auth/otp/verify", { challenge_id: challengeId, code, remember, device_id, force });
      if (data.session_conflict) {
        return { ok: false, sessionConflict: true };
      }
      if (data.device_id) {
        // Salva il token del dispositivo fidato in un cookie con scadenza 30 giorni.
        setDeviceCookie(data.device_id, REMEMBER_DAYS);
        localStorage.setItem(DEVICE_KEY, data.device_id);
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      return { ok: false, error: msg };
    }
  };

  const applyAuth = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
  };

  const logout = () => {
    if (user?.impersonating && localStorage.getItem(SA_TOKEN_KEY)) {
      api.post("/auth/logout").catch(() => {}).finally(() => restoreSuperadmin());
      return;
    }
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const can = useCallback(
    (section) => {
      if (!user) return false;
      if (user.role === "titolare" || user.role === "amministratore") return true;
      if (user.role === "operatore") {
        if (!section) return true;
        const lvl = user.permissions && user.permissions[section];
        return lvl === true || lvl === "read" || lvl === "write";
      }
      return false;
    },
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, error, login, requestOtp, verifyOtp, applyAuth, logout, can, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
